import React, { useEffect, useState } from 'react';
import { Button, Form, Label, Pagination, Popup, Table } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { API, isRoot, showError, showSuccess } from '../helpers';

import { ITEMS_PER_PAGE } from '../constants';
import { renderGroup, renderNumber, renderQuota, renderText } from '../helpers/render';

function renderRole(role) {
  switch (role) {
    case 1:
      return <Label>普通用户</Label>;
    case 10:
      return <Label color='yellow'>租户管理员</Label>;
    case 100:
      return <Label color='orange'>超级管理员</Label>;
    default:
      return <Label color='red'>未知身份</Label>;
  }
}

// readOnly=true 时用于租户管理员看板：隐藏写操作与租户过滤器，接口自动限定本租户
const UsersTable = ({ readOnly = false }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [filterGroup, setFilterGroup] = useState('');
  const [groupOptions, setGroupOptions] = useState([]);
  const [filterTenant, setFilterTenant] = useState('');
  const [tenantOptions, setTenantOptions] = useState([]);
  // the keyword/group/tenant actually applied to the currently loaded rows,
  // so pagination keeps querying the same result set
  const [appliedSearch, setAppliedSearch] = useState({ keyword: '', group: '', tenant: '' });

  const fetchGroups = async () => {
    try {
      const res = await API.get('/api/group/');
      setGroupOptions(
        res.data.data.map((g) => ({ key: g, text: g, value: g }))
      );
    } catch (e) {
      showError(e.message);
    }
  };

  const fetchTenants = async () => {
    try {
      const res = await API.get('/api/tenant/');
      setTenantOptions(
        (res.data.data || []).map((t) => ({
          key: t.key,
          text: t.name ? `${t.name} (${t.key})` : t.key,
          value: t.key
        }))
      );
    } catch (e) {
      showError(e.message);
    }
  };

  const buildUsersURL = (startIdx, search) => {
    const params = new URLSearchParams();
    if (search.keyword) params.append('keyword', search.keyword);
    if (search.group) params.append('group', search.group);
    if (!readOnly && search.tenant) params.append('tenant', search.tenant);
    params.append('p', startIdx);
    const base = readOnly ? '/api/tenant_admin/users' : '/api/user/search';
    return `${base}?${params.toString()}`;
  };

  const loadUsers = async (startIdx, search = appliedSearch) => {
    const res = await API.get(buildUsersURL(startIdx, search));
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setUsers(data);
      } else {
        setUsers((prev) => [...prev, ...data]);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(users.length / ITEMS_PER_PAGE) + 1) {
        // In this case we have to load more data and then append them.
        await loadUsers(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  useEffect(() => {
    loadUsers(0)
      .then()
      .catch((reason) => {
        showError(reason);
      });
    if (!readOnly) {
      fetchGroups();
      fetchTenants();
    }
  }, []);

  const manageUser = (username, action, idx) => {
    (async () => {
      const res = await API.post('/api/user/manage', {
        username,
        action
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess('操作成功完成！');
        let user = res.data.data;
        let newUsers = [...users];
        let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
        if (action === 'delete') {
          newUsers[realIdx].deleted = true;
        } else {
          newUsers[realIdx].status = user.status;
          newUsers[realIdx].role = user.role;
        }
        setUsers(newUsers);
      } else {
        showError(message);
      }
    })();
  };

  const renderStatus = (status) => {
    switch (status) {
      case 1:
        return <Label basic>已激活</Label>;
      case 2:
        return (
          <Label basic color='red'>
            已封禁
          </Label>
        );
      default:
        return (
          <Label basic color='grey'>
            未知状态
          </Label>
        );
    }
  };

  const searchUsers = async (
    keyword = searchKeyword,
    group = filterGroup,
    tenant = filterTenant
  ) => {
    const search = { keyword, group, tenant };
    setSearching(true);
    setAppliedSearch(search);
    setActivePage(1);
    await loadUsers(0, search);
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortUser = (key) => {
    if (users.length === 0) return;
    setLoading(true);
    let sortedUsers = [...users];
    sortedUsers.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedUsers[0].id === users[0].id) {
      sortedUsers.reverse();
    }
    setUsers(sortedUsers);
    setLoading(false);
  };

  return (
    <>
      <Form>
        <Form.Group widths='equal'>
          <Form.Input
            icon='search'
            iconPosition='left'
            placeholder='搜索用户的 ID，用户名，显示名称，邮箱地址，以及备注 ...'
            value={searchKeyword}
            loading={searching}
            onChange={handleKeywordChange}
            onKeyDown={(e) => { if (e.key === 'Enter') searchUsers(e.target.value.trim(), filterGroup, filterTenant); }}
          />
          {!readOnly && (
            <>
              <Form.Select
                search
                clearable
                placeholder='按分组过滤 ...'
                value={filterGroup}
                options={groupOptions}
                onChange={(e, { value }) => {
                  const group = value || '';
                  setFilterGroup(group);
                  searchUsers(searchKeyword, group, filterTenant);
                }}
              />
              <Form.Select
                search
                clearable
                placeholder='按租户过滤 ...'
                value={filterTenant}
                options={tenantOptions}
                onChange={(e, { value }) => {
                  const tenant = value || '';
                  setFilterTenant(tenant);
                  searchUsers(searchKeyword, filterGroup, tenant);
                }}
              />
            </>
          )}
        </Form.Group>
      </Form>

      <Table basic compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('id');
              }}
            >
              ID
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('username');
              }}
            >
              用户名
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('group');
              }}
            >
              分组
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('tenant');
              }}
            >
              租户
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('quota');
              }}
            >
              统计信息
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('role');
              }}
            >
              用户角色
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortUser('status');
              }}
            >
              状态
            </Table.HeaderCell>
            {!readOnly && <Table.HeaderCell>操作</Table.HeaderCell>}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {users
            .slice(
              (activePage - 1) * ITEMS_PER_PAGE,
              activePage * ITEMS_PER_PAGE
            )
            .map((user, idx) => {
              if (user.deleted) return <></>;
              return (
                <Table.Row key={user.id}>
                  <Table.Cell>{user.id}</Table.Cell>
                  <Table.Cell>
                    <Popup
                      content={<>
                        <div>{user.email ? user.email : '未绑定邮箱地址'}</div>
                        {isRoot() && user.remark && <div style={{marginTop: '4px', color: '#888'}}>备注: {user.remark}</div>}
                      </>}
                      key={user.username}
                      header={user.display_name ? user.display_name : user.username}
                      trigger={<span>{renderText(user.username, 15)}</span>}
                      hoverable
                    />
                  </Table.Cell>
                  <Table.Cell>{renderGroup(user.group)}</Table.Cell>
                  <Table.Cell>{user.tenant || '-'}</Table.Cell>
                  {/*<Table.Cell>*/}
                  {/*  {user.email ? <Popup hoverable content={user.email} trigger={<span>{renderText(user.email, 24)}</span>} /> : '无'}*/}
                  {/*</Table.Cell>*/}
                  <Table.Cell>
                    <Popup content='剩余额度' trigger={<Label basic>{renderQuota(user.quota)}</Label>} />
                    <Popup content='已用额度' trigger={<Label basic>{renderQuota(user.used_quota)}</Label>} />
                    <Popup content='请求次数' trigger={<Label basic>{renderNumber(user.request_count)}</Label>} />
                  </Table.Cell>
                  <Table.Cell>{renderRole(user.role)}</Table.Cell>
                  <Table.Cell>{renderStatus(user.status)}</Table.Cell>
                  {!readOnly && (
                    <Table.Cell>
                      <div>
                        <Button
                          size={'small'}
                          onClick={() => {
                            manageUser(
                              user.username,
                              user.status === 1 ? 'disable' : 'enable',
                              idx
                            );
                          }}
                          disabled={user.role === 100}
                        >
                          {user.status === 1 ? '禁用' : '启用'}
                        </Button>
                        <Button
                          size={'small'}
                          as={Link}
                          to={'/user/edit/' + user.id}
                        >
                          编辑
                        </Button>
                      </div>
                    </Table.Cell>
                  )}
                </Table.Row>
              );
            })}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={readOnly ? '7' : '8'}>
              {!readOnly && (
                <Button size='small' as={Link} to='/user/add' loading={loading}>
                  添加新的用户
                </Button>
              )}
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={onPaginationChange}
                size='small'
                siblingRange={1}
                totalPages={
                  Math.ceil(users.length / ITEMS_PER_PAGE) +
                  (users.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                }
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default UsersTable;
